import moment, { Moment } from 'moment-timezone'
import { ConsultationFormStatus, ExternalBookingPaymentStatus, ExternalBookingStatus } from '../../graphQL/generated/enums'
import { generateYearMonthOptions } from '../../shared-lib/timeHelpers'
import BookingModel from '../stores/BookingStore/BookingModel'
import ExternalBookingModel from '../stores/ExternalBookingStore/ExternalBookingModel'
import { Filters } from '../viewModels/BaseBookingsScreenVM'
import voca from 'voca'

export const bookingStatusFilter = ['All', 'Confirmed', 'Requested', 'Instant Requested', 'Completed', 'Cancelled', 'Cancel Requested', 'To Complete', 'Reschedule Requested'] as const

export const monthOptions = generateYearMonthOptions(24, 31, 'MMM YY').reverse()
export const bookingTimeFilter = ['All', 'Today', 'Tomorrow', 'Next 30 days', 'Last 30 days', 'This year', 'Last year', 'Specific month'] as const

export const UNASSIGNED_PROFESSIONAL = 'unassigned'

export type BookingStatusFilter = typeof bookingStatusFilter[number]
export type BookingTimeFilter = typeof bookingTimeFilter[number]

export const splitFiltersText = (selectedFilters: Filters, _isSaasUser?: boolean) => {
  const timeTexts = timeFilterText(selectedFilters.bookingTime, selectedFilters.monthSelected)
  const timeText = voca.capitalize((timeTexts[0] + timeTexts[1]) || 'All time')

  const statusText = selectedFilters.bookingStatus === 'All' ? 'All statuses' : `${voca.capitalize(selectedFilters.bookingStatus)} bookings`

  return {
    timeText,
    statusText,
  }
}

export const statusFilterLabel = (bookingStatuses: BookingStatusFilter): string => {
  if (bookingStatuses === 'All') {
    return 'All bookings'
  }

  return `${bookingStatuses} bookings`
}

export const timeFilterText = (bookingTime: BookingTimeFilter, monthSelected: string): string[] => {
  switch (bookingTime) {
    case 'Last 30 days':
      return ['in the ', 'last 30 days']
    case 'Next 30 days':
      return ['in the ', 'next 30 days']
    case 'This year':
      return ['this ', 'year']
    case 'Last year':
      return ['last ', 'year']
    case 'Specific month':
      return ['in ', moment(monthSelected, 'MMM YY').format('MMMM YYYY')]
    case 'Today':
      return ['for ', 'today']
    case 'Tomorrow':
      return ['for ', 'tomorrow']
    case 'All':
      return ['', '']
  }
}

export const filterBookings = (bookings: (BookingModel | ExternalBookingModel)[], filters: Filters, events = false) => {
  const { bookingStatus, bookingTime, monthSelected, extraFilters, professional } = filters

  const statusFilter = statusFilterHelper(bookingStatus)

  const timeFilter = timeFilteredHelper(bookingTime, monthSelected)

  const extraFilter = extraFilteredHelper(extraFilters)

  const filterEvents = eventsFilter(events)

  const professionalFilter = professionalFilterHelper(professional)

  return bookings.filter(statusFilter).filter(timeFilter).filter(extraFilter).filter(filterEvents).filter(professionalFilter)
}

const eventsFilter = (events: boolean) => {
  const filter = (booking: BookingModel | ExternalBookingModel): boolean => {
    return events ? booking.data.type === 'event' : booking.data.type !== 'event'
  }
  return filter
}

const statusFilterHelper = (bookingStatus: BookingStatusFilter) => {
  const filter = (booking: BookingModel | ExternalBookingModel): boolean => {
    switch (bookingStatus) {
      case 'All':
        return !['DRAFT', 'RESCHEDULE'].includes(booking.data.status)
      case 'Confirmed':
        return ['ACCEPTED', 'IN_TRANSIT', 'ARRIVED'].includes(booking.data.status)
      case 'Requested':
        return booking.data.status === 'REQUESTED'
      case 'Instant Requested':
        return booking.data.status === 'APPROVAL_PENDING' && !!booking.data.professionalId && (booking.data.type === 'instant' || booking.data.type === 'assigned')
      case 'Completed':
        return booking.data.status === 'COMPLETED'
      case 'Cancelled':
        return booking.data.status === 'CANCELLED'
      case 'Reschedule Requested':
        return !!(booking as ExternalBookingModel).data.rescheduleBookingId
      case 'Cancel Requested':
        return booking.data.status === 'CANCEL_REQUESTED'
      case 'To Complete':
        return ['TO_COMPLETE'].includes(booking.data.status)
    }
  }
  return filter
}

const timeFilteredHelper = (bookingTime: BookingTimeFilter, monthSelected: string) => {
  const filter = (booking: BookingModel | ExternalBookingModel) => {

    const getDateFilter = (bookingTime: BookingTimeFilter): { ltDate: Moment, gtDate: Moment } => {
      const startOfToday = moment().startOf('day')
      const endOfToday = moment().endOf('day')

      const defaults = {
        gtDate: moment().subtract(30, 'years'),
        ltDate: moment().add(30, 'years'),
      }

      // For any changes to the filters, also update dateFilter in BookingsScreenVM
      switch (bookingTime) {
        case 'All':
          return defaults
        case 'Last 30 days':
          return { gtDate: moment(startOfToday).subtract(30, 'days'), ltDate: moment() }
        case 'Next 30 days':
          return { gtDate: moment(), ltDate: moment(startOfToday).add(30, 'days') }
        case 'This year':
          return { gtDate: moment().startOf('year'), ltDate: moment().endOf('year') }
        case 'Last year':
          return { gtDate: moment().subtract(1, 'year').startOf('year'), ltDate: moment().subtract(1, 'year').endOf('year') }
        case 'Specific month':
          return { gtDate: moment(monthSelected, 'MMM YY').startOf('month'), ltDate: moment(monthSelected, 'MMM YY').endOf('month') }
        case 'Today':
          return { gtDate: startOfToday, ltDate: endOfToday }
        case 'Tomorrow':
          return { gtDate: moment(startOfToday).add(1, 'day'), ltDate: moment(endOfToday).add(1, 'day') }
      }
    }

    const dateFilter = getDateFilter(bookingTime)
    const bookingDate = moment(booking.data.date)

    return bookingDate.isAfter(dateFilter.gtDate) && bookingDate.isBefore(dateFilter.ltDate)
  }

  return filter
}

const extraFilteredHelper = (extraFilters: Filters['extraFilters']) => {
  const filter = (booking: BookingModel | ExternalBookingModel): boolean => {
    const externalBooking = (booking as ExternalBookingModel)

    switch (extraFilters) {
      case 'Unread':
        return (booking.data as any).chat && !!(booking.data as any).chat.messageCount.pro.unreadMessages
      case 'OutstandingPayments':
        return !!(externalBooking.data.paymentStatus === ExternalBookingPaymentStatus.DepositNotPaid && externalBooking.data.status === ExternalBookingStatus.Accepted) ||
          (externalBooking.data.paymentStatus !== ExternalBookingPaymentStatus.FullyPaid && [ExternalBookingStatus.ToComplete, ExternalBookingStatus.Completed].includes(externalBooking.data.status))
      case 'OutstandingConsultationForms':
        return [ConsultationFormStatus.ReminderSent, ConsultationFormStatus.Requested].includes(externalBooking.data.consultationFormStatus) && externalBooking.data.status === ExternalBookingStatus.Accepted
      case '':
        return true
    }
  }

  return filter
}

const professionalFilterHelper = (professional: Filters['professional']) => {
  const filter = (booking: BookingModel | ExternalBookingModel): boolean => {
    if (!professional) return true

    const allocated = (booking as ExternalBookingModel).data.allocatedProfessionalId

    return professional === UNASSIGNED_PROFESSIONAL ? !allocated : allocated === professional
  }

  return filter
}

export const sortBookingsByDate = (a: BookingModel | ExternalBookingModel, b: BookingModel | ExternalBookingModel) => {
  return new Date(a.data.date || 0).getTime() - new Date(b.data.date || 0).getTime() // Oldest first
}

const todaysBookingsStatus = ['ACCEPTED', 'IN_TRANSIT', 'ARRIVED', 'COMPLETED']

export const todaysBookingsFilter = (booking: BookingModel | ExternalBookingModel) => {
  return todaysBookingsStatus.includes(booking.data.status) && moment(booking.data.date).isSame(moment(), 'day')
}

const next7DaysBookingsStatus = ['ACCEPTED']

export const next7DaysBookingsFilter = (booking: BookingModel | ExternalBookingModel) => {
  return next7DaysBookingsStatus.includes(booking.data.status) && moment(booking.data.date).isAfter(moment(), 'day') && moment(booking.data.date).isBefore(moment().add(7, 'days'), 'day')
}
